import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Logo } from '@/components/Logo';

/**
 * Handles email confirmation links from the auth-send-email hook.
 * URL format: /auth/confirm?token_hash=XXX&type=signup&redirect_to=...
 *
 * Calls supabase.auth.verifyOtp() with the token_hash, then redirects
 * to /auth/callback flow (recovery → set-password, otherwise → /app).
 */
export default function AuthConfirm() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get('token_hash');
    const type = params.get('type') as 'signup' | 'recovery' | 'magiclink' | 'invite' | 'email_change' | undefined;

    if (!tokenHash || !type) {
      navigate('/login', { replace: true });
      return;
    }

    const verify = async () => {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type === 'signup' ? 'email' : type,
      });

      if (verifyError) {
        console.error('[auth-confirm] verifyOtp failed:', verifyError.message);
        setError(verifyError.message);
        return;
      }

      if (type === 'recovery') {
        navigate('/auth/set-password', { replace: true });
      } else {
        navigate('/app', { replace: true });
      }
    };

    verify();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-wt-bg-primary text-wt-text-primary p-4">
        <Logo variant="fullTransparent" size="h-12" className="mb-6 opacity-90" />
        <p className="font-sans text-red-400 mb-4">Confirmation failed</p>
        <p className="font-sans text-wt-text-secondary text-sm max-w-md text-center">{error}</p>
        <button
          onClick={() => navigate('/login', { replace: true })}
          className="mt-6 px-4 py-2 bg-wt-solar-gold text-wt-bg-primary rounded-lg font-medium text-sm"
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-wt-bg-primary text-wt-text-primary p-4">
      <Logo variant="fullTransparent" size="h-12" className="mb-6 opacity-90" />
      <p className="font-sans text-wt-text-secondary">Confirming your email...</p>
    </div>
  );
}
